import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Actions
import { showModal } from '../../../../actions/ModalActions';
import {
  loadGuestList,
  onSeatReorder,
  onTableReorder,
  addPeopleToTable,
  removeSeatFromTable,
  reorderSeatsInTable,
  selectGuest,
  startTableDrag,
  updateGuestList,
  updateGuestListAction,
  updateTables,
  updateSeats,
  hideBanner,
  showBanner,
  endDrag,
  startDrag,
} from '../../../../actions/SeatingChartActions';

// Selectors
import { getTablesOfSelectedGuests } from '../../../../selectors/seatingchart/SeatingChartSelector';

// Components
import Table from './Table/Table';

import './Tables.styles.less';

const Tables = ({
  activeSeatInputUUID,
  directlyUpdateGuestList,
  draggingSeatInProgress,
  guestsByGuestDistinction,
  guestsByID,
  onEditTable,
  onEndDrag,
  onDragTable,
  onPushSeatsToTable,
  onRemoveSeat,
  onReorderSeat,
  onReorderSeats,
  onReorderTable,
  onSelectGuest,
  onStartDrag,
  onUpdateGuestList,
  onUpdateSeats,
  onUpdateTables,
  seatsByUUID,
  singleDragOriginTable,
  selectedGuests,
  tablesByUUID,
  tableUUIDs,
  tablesOfSelectedGuests,
  onHideBanner,
  onShowBanner,
  flowRunId,
  updateFlowRunId,
}) => {
  // Reordering tables
  const moveTable = () => {
    const updatedTables = tableUUIDs.map((uuid) => {
      const table = tablesByUUID[uuid];
      return {
        uuid,
        display_order: table.display_order,
        name: table.name,
        x: table.x,
        y: table.y,
        shape: table.shape,
        num_seats: table.num_seats
      };
    });
    onUpdateTables(updatedTables);
  };
  // Removing a single seat (via the 'x')
  const removeSeat = (seat) => {
    const clearedSeat = {
      ...seat,
      guest_id: null,
      couple_role: null,
      display_name: null,
      isEmpty: true,
    };
    onRemoveSeat(seat.uuid);
    directlyUpdateGuestList([clearedSeat]);
    onUpdateSeats(); // Call to update the change in the backend
  };
  // Adding seats or singular/bulk removing (draging to guest rail)
  const pushSeat = (tableUUID) => {
    // Front end changes
    onPushSeatsToTable(tableUUID, guestsByGuestDistinction);
    onUpdateGuestList();
    // Call to update the change in the backend
    onUpdateSeats();
  };
  // Re-ordering within a table
  const reorderSeats = (seats) => {
    onReorderSeats(seats);
    onUpdateSeats();
  };

  return (
    <div className="sc__tables">
      {tableUUIDs.length > 0 && tablesByUUID && tableUUIDs.map((uuid, index) => (
        <Table
          key={uuid}
          activeSeatInputUUID={activeSeatInputUUID}
          index={index}
          table={tablesByUUID[uuid]}
          seats={tablesByUUID[uuid].seats}
          seatsByUUID={seatsByUUID}
          guestsByID={guestsByID}
          moveTable={moveTable}
          pushSeat={pushSeat}
          removeSeat={removeSeat}
          onReorderSeats={reorderSeats}
          onEditTable={onEditTable}
          onReorderTable={onReorderTable}
          onReorderSeat={onReorderSeat}
          onSelectGuest={onSelectGuest}
          singleDragOriginTable={singleDragOriginTable}
          onStartDrag={onStartDrag}
          onEndDrag={onEndDrag}
          draggingSeatInProgress={draggingSeatInProgress}
          onDragTable={onDragTable}
          selectedGuests={selectedGuests}
          tablesOfSelectedGuests={tablesOfSelectedGuests}
          hideBanner={onHideBanner}
          showBanner={onShowBanner}
          flowRunId={flowRunId}
          updateFlowRunId={updateFlowRunId}
        />
      ))}
    </div>
  );
};

Tables.propTypes = {
  activeSeatInputUUID: PropTypes.string,
  guestsByGuestDistinction: PropTypes.objectOf(PropTypes.any),
  guestsByID: PropTypes.shape({
    rsvpType: PropTypes.string,
  }),
  onEditTable: PropTypes.func,
  onReorderTable: PropTypes.func,
  onReorderSeat: PropTypes.func,
  onReorderSeats: PropTypes.func,
  onSelectGuest: PropTypes.func,
  onUpdateTables: PropTypes.func,
  onPushSeatsToTable: PropTypes.func,
  onRemoveSeat: PropTypes.func,
  onUpdateGuestList: PropTypes.func,
  onUpdateSeats: PropTypes.func,
  selectedGuests: PropTypes.shape({
    couple: PropTypes.arrayOf(PropTypes.string),
    guests: PropTypes.arrayOf(PropTypes.number)
  }),
  singleDragOriginTable: PropTypes.string,
  tableUUIDs: PropTypes.arrayOf(PropTypes.string),
  tablesByUUID: PropTypes.objectOf(PropTypes.any),
  seatsByUUID: PropTypes.objectOf(PropTypes.any),
  onDragTable: PropTypes.func,
  onEndDrag: PropTypes.func,
  onStartDrag: PropTypes.func,
  draggingSeatInProgress: PropTypes.bool,
  tablesOfSelectedGuests: PropTypes.arrayOf(PropTypes.string),
  directlyUpdateGuestList: PropTypes.func,
  flowRunId: PropTypes.string,
  updateFlowRunId: PropTypes.func,
};


const mapStateToProps = state => ({
  activeSeatInputUUID: state.seatingChart.activeSeatInput,
  draggingSeatInProgress: state.seatingChart.draggingSeatInProgress,
  guestsByGuestDistinction: state.guestRail.guestsByGuestDistinction,
  guestsByID: state.seatingChart.guestsByID,
  seatsByUUID: state.seatingChart.seatsByUUID,
  selectedGuests: state.seatingChart.selectedGuests,
  singleDragOriginTable: state.seatingChart.singleDragOriginTable,
  tableUUIDs: state.seatingChart.orderedTableUUIDs,
  tablesByUUID: state.seatingChart.tablesByUUID,
  tablesOfSelectedGuests: getTablesOfSelectedGuests(state.seatingChart),
});

const mapDispatchToProps = dispatch => ({
  onDragTable: () => dispatch(startTableDrag()),
  onEditTable: table => dispatch(showModal('EDIT_TABLE_MODAL', { table }, { size: 'sm' })),
  onLoadGuestList: () => dispatch(loadGuestList()),
  onPushSeatsToTable: tableUUID => dispatch(addPeopleToTable(tableUUID)),
  onReorderTable: (sourceIndex, hoverIndex) => { dispatch(onTableReorder(sourceIndex, hoverIndex)); },
  onRemoveSeat: seatUUID => dispatch(removeSeatFromTable(seatUUID)),
  onSelectGuest: (guest, guestGroup, isCouple) => { dispatch(selectGuest(guest, guestGroup, isCouple)); },
  onReorderSeat: (sourceIndex, hoverIndex, tableUUID, forceReorder) => dispatch(onSeatReorder(sourceIndex, hoverIndex, tableUUID, forceReorder)),
  onReorderSeats: seats => dispatch(reorderSeatsInTable(seats)),
  onUpdateGuestList: () => dispatch(updateGuestListAction()),
  directlyUpdateGuestList: updatedSeats => dispatch(updateGuestList(updatedSeats)),
  onUpdateSeats: () => dispatch(updateSeats()),
  onUpdateTables: tables => dispatch(updateTables(tables, false)),
  onHideBanner: id => dispatch(hideBanner(id)),
  onShowBanner: (text, type, id) => dispatch(showBanner(text, type, id)),
  onStartDrag: people => dispatch(startDrag(people)),
  onEndDrag: () => dispatch(endDrag()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Tables);



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/Tables/Tables.jsx